import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { LucideAngularModule, X } from 'lucide-angular';

export interface SearchMultiselectOption {
  id: number | string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-search-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, LucideAngularModule],
  templateUrl: './search-multiselect.component.html',
  styleUrls: ['./search-multiselect.component.scss']
})
export class SearchMultiselectComponent {
  readonly removeIcon = X;
  readonly selectAllValue = '__select_all_filtered__';

  @Input() label = '';
  @Input() searchLabel = 'Buscar';
  @Input() searchPlaceholder = 'Digite para filtrar';
  @Input() selectPlaceholder = 'Selecione';
  @Input() clearSelectionLabel = 'Limpar seleção';
  @Input() selectAllLabel = 'Selecionar todos os filtrados';
  @Input() clearFilteredLabel = 'Limpar todos os filtrados';
  @Input() loadingMessage = 'Carregando opções...';
  @Input() emptyMessage = 'Nenhuma opção disponível.';
  @Input() blockedMessage = '';
  @Input() selectLabel = '';
  @Input() options: SearchMultiselectOption[] = [];
  @Input() selectedValues: Array<number | string> = [];
  @Input() required = false;
  @Input() invalid = false;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() blocked = false;
  /** @deprecated Badges agora são sempre exibidos dentro do trigger. Este input não tem efeito. */
  @Input() showSelectedBadges = false;
  /** Sufixo exibido quando mais de um item está selecionado. Ex: "Alunos selecionados" → "3 Alunos selecionados" */
  @Input() selectedCountLabel = 'selecionados';

  @Output() selectedValuesChange = new EventEmitter<Array<number | string>>();

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  searchTerm = '';

  get filteredOptions(): SearchMultiselectOption[] {
    const term = this.searchTerm.trim().toLocaleLowerCase();
    return this.options
      .filter((option) => !term || option.label.toLocaleLowerCase().includes(term))
      .sort((optionA, optionB) => {
        const optionASelected = this.selectedValues.includes(optionA.id);
        const optionBSelected = this.selectedValues.includes(optionB.id);

        if (optionASelected !== optionBSelected) {
          return optionASelected ? -1 : 1;
        }

        return optionA.label.localeCompare(optionB.label, 'pt-BR', { sensitivity: 'base' });
      });
  }

  get allFilteredSelected(): boolean {
    return !!this.filteredOptions.length && this.filteredOptions.every((option) => this.selectedValues.includes(option.id));
  }

  /** Em ordem de options. */
  get selectedOptions(): SearchMultiselectOption[] {
    return this.options.filter((option) => this.selectedValues.includes(option.id));
  }

  /** Texto exibido no trigger: vazio→placeholder (gerenciado pelo mat-select), 1→nome, N→"N {selectedCountLabel}" */
  get triggerLabel(): string {
    const count = this.selectedValues.length;
    if (count === 0) {
      return '';
    }
    if (count === 1) {
      return this.selectedOptions[0]?.label ?? '';
    }
    return `${count} ${this.selectedCountLabel}`;
  }

  trackById(_index: number, option: SearchMultiselectOption): number | string {
    return option.id;
  }

  onSelectionChange(event: MatSelectChange): void {
    const values = Array.isArray(event.value) ? event.value : [];

    if (values.includes(this.selectAllValue)) {
      this.toggleSelectAllFiltered();
      return;
    }

    const normalized = values.filter((value) => value !== this.selectAllValue);
    this.selectedValuesChange.emit(this.getUniqueValues(normalized));
  }

  clearSelection(): void {
    this.selectedValuesChange.emit([]);
  }

  removeValue(id: number | string): void {
    this.selectedValuesChange.emit(this.selectedValues.filter((value) => value !== id));
  }

  clearSelectionFromTrigger(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.clearSelection();
  }

  stopMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onSelectOpenedChange(opened: boolean): void {
    this.searchTerm = '';
    if (opened) {
      setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 80);
    }
  }

  private toggleSelectAllFiltered(): void {
    if (this.allFilteredSelected) {
      const filteredIds = new Set(this.filteredOptions.map((option) => option.id));
      this.selectedValuesChange.emit(this.selectedValues.filter((value) => !filteredIds.has(value)));
      return;
    }

    this.selectedValuesChange.emit(this.getUniqueValues([
      ...this.selectedValues,
      ...this.filteredOptions.map((option) => option.id)
    ]));
  }

  private getUniqueValues(values: Array<number | string>): Array<number | string> {
    return values.filter((value, index, array) => array.indexOf(value) === index);
  }
}